import React from 'react';
import { Modal, Form, Select, DatePicker, Input, Button, InputNumber } from 'antd';
import { ContractorPaymentDto, ContractorProjectDetailsDto, PaymentMethod } from 'api/models';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

interface UpdateContractorPaymentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  payment: ContractorPaymentDto | null;
  projects: ContractorProjectDetailsDto[];
  onProjectSelect: (value: number) => void;
}

export const UpdateContractorPaymentModal: React.FC<UpdateContractorPaymentModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  projects,
  onProjectSelect,
  payment,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentMethod>(
    payment?.paymentMethod ?? PaymentMethod.Cash
  );

  React.useEffect(() => {
    if (payment) {
      form.setFieldsValue({
        ...payment,
        dueDate: payment.dueDate ? dayjs(payment.dueDate) : null,
      });
      setSelectedPaymentMethod(payment.paymentMethod as PaymentMethod);
    }
  }, [payment, form]);

  const handlePaymentMethodChange = (value: PaymentMethod) => {
    setSelectedPaymentMethod(value);
    if (value === PaymentMethod.Cash) {
      form.setFieldValue('transactionReference', undefined);
    }
  };

  React.useEffect(() => {
    if (payment) {
      form.setFieldsValue({
        ...payment,
        dueDate: payment.dueDate ? dayjs(payment.dueDate) : null,
      });
    }
  }, [payment, form]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('payments:update', 'Update Payment')}
      open={visible}
      onCancel={handleCancel}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
      >
        <Form.Item name="projectId" label={t('payments:project', 'Project')} rules={[{ required: true }]}>
          <Select onChange={onProjectSelect}>
            {projects?.map(project => (
              <Select.Option key={project.projectId} value={project.projectId}>
                {project.projectName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          name="amount" 
          label={t('payments:amount', 'Amount')} 
          rules={[{ required: true }]}
        >
          <InputNumber
            className="w-full"
            style={{ width: '100%' }}
            formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value!.replace(/€\s?|(,*)/g, '')}
          />
        </Form.Item>

        <Form.Item 
          name="dueDate" 
          label={t('payments:due-date', 'Due Date')} 
          rules={[{ required: true }]}
        >
          <DatePicker className="w-full" />
        </Form.Item>

        <Form.Item 
          name="paymentMethod" 
          label={t('payments:method', 'Payment Method')} 
          rules={[{ required: true }]}
        >
          <Select onChange={handlePaymentMethodChange}>
            <Select.Option value={PaymentMethod.Cash}>
              {t('payments:cash', 'Cash')}
            </Select.Option>
            <Select.Option value={PaymentMethod.Bank}>
              {t('payments:bank', 'Bank')}
            </Select.Option>
            <Select.Option value={PaymentMethod.Crypto}>
              {t('payments:crypto', 'Crypto')}
            </Select.Option>
            <Select.Option value={PaymentMethod.Other}>
              {t('payments:other', 'Other')}
            </Select.Option>
          </Select>
        </Form.Item>

        {selectedPaymentMethod !== PaymentMethod.Cash && payment?.isPaid && (
          <Form.Item 
            name="transactionReference" 
            label={t('payments:transaction-reference', 'Transaction Reference')}
            rules={[{ required: true, message: t('payments:transaction-reference-required', 'Transaction reference is required for non-cash payments') }]}
          >
            <Input />
          </Form.Item>
        )}

        <Form.Item 
          name="notes" 
          label={t('payments:notes', 'Notes')}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t('payments:update', 'Update')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};